// ---------------------------------------------------------------------------
// SEO Meta Builder
// ---------------------------------------------------------------------------

import type { SeoMeta } from "../schema/index.js";
import { attr, text } from "./escape.js";

export function buildSeoMeta(
  pageTitle: string,
  seo: SeoMeta,
  pageUrl?: string,
): string {
  const parts: string[] = [];

  // Description
  if (seo.description) {
    parts.push(`<meta name="description" content="${attr(seo.description)}">`);
  }

  // Robots
  if (seo.robots) {
    parts.push(`<meta name="robots" content="${attr(seo.robots)}">`);
  }

  // Canonical
  if (seo.canonicalUrl) {
    parts.push(`<link rel="canonical" href="${attr(seo.canonicalUrl)}">`);
  }

  // Open Graph
  parts.push(`<meta property="og:title" content="${attr(seo.title ?? pageTitle)}">`);
  if (seo.description) {
    parts.push(`<meta property="og:description" content="${attr(seo.description)}">`);
  }
  if (seo.ogImage) {
    parts.push(`<meta property="og:image" content="${attr(seo.ogImage)}">`);
  }
  parts.push(`<meta property="og:type" content="${attr(seo.ogType ?? "website")}">`);
  if (pageUrl) {
    parts.push(`<meta property="og:url" content="${attr(pageUrl)}">`);
  }

  // Twitter Card
  parts.push(`<meta name="twitter:card" content="${attr(seo.twitterCard ?? "summary_large_image")}">`);
  parts.push(`<meta name="twitter:title" content="${attr(seo.title ?? pageTitle)}">`);
  if (seo.description) {
    parts.push(`<meta name="twitter:description" content="${attr(seo.description)}">`);
  }
  if (seo.ogImage) {
    parts.push(`<meta name="twitter:image" content="${attr(seo.ogImage)}">`);
  }

  return parts.join("\n");
}

// ---------------------------------------------------------------------------
// JSON-LD builder
// ---------------------------------------------------------------------------

export function buildJsonLd(schemas: Record<string, unknown>[]): string {
  if (schemas.length === 0) return "";

  return schemas
    .map(
      (schema) =>
        `<script type="application/ld+json">${JSON.stringify(schema).replace(/<\/script/gi, "<\\/script")}</script>`,
    )
    .join("\n");
}

export function buildWebPageSchema(
  pageTitle: string,
  description?: string,
  pageUrl?: string,
): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: pageTitle,
  };
  if (description) schema.description = description;
  if (pageUrl) schema.url = pageUrl;
  return schema;
}

export function buildFaqSchema(
  items: Array<{ question: string; answer: string }>,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

// ---------------------------------------------------------------------------
// Article JSON-LD (blog posts)
// ---------------------------------------------------------------------------

export interface ArticleSchemaInput {
  headline: string;
  description: string | null;
  authorName: string;
  publishedAt: Date;
  updatedAt: Date;
  featuredImage: string | null;
  articleUrl: string;
  publisherName: string;
  publisherLogoUrl: string | null;
}

export function buildArticleSchema(input: ArticleSchemaInput): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: input.headline,
    author: { "@type": "Person", name: input.authorName },
    datePublished: input.publishedAt.toISOString(),
    dateModified: input.updatedAt.toISOString(),
    publisher: {
      "@type": "Organization",
      name: input.publisherName,
      ...(input.publisherLogoUrl ? { logo: { "@type": "ImageObject", url: input.publisherLogoUrl } } : {}),
    },
  };
  if (input.description) schema.description = input.description;
  if (input.featuredImage) schema.image = input.featuredImage;
  if (input.articleUrl) schema.url = input.articleUrl;
  return schema;
}

// ---------------------------------------------------------------------------
// Breadcrumb JSON-LD
// ---------------------------------------------------------------------------

export interface BreadcrumbItem {
  position: number;
  name: string;
  item: string;
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item) => ({
      "@type": "ListItem",
      position: item.position,
      name: item.name,
      item: item.item,
    })),
  };
}
